import React, { useState } from 'react';
import { database } from '../../../firebaseConfig';
import { ref, set, push, onValue } from 'firebase/database';
import Papa from 'papaparse';

const CsvHandler = ({ 
  userId, 
  entityType, // 'services' or 'microservices'
  onSuccess, 
  onError 
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successCount, setSuccessCount] = useState(0);
  const [microservices, setMicroservices] = useState([]);

  // Load microservices for services
  React.useEffect(() => {
    if (entityType === 'services' && userId) {
      const microservicesRef = ref(database, `/World/${userId}/microservices`);
      const unsubscribe = onValue(microservicesRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const microservicesArray = Object.entries(data).map(([id, ms]) => ({
            id,
            ...ms
          }));
          setMicroservices(microservicesArray);
        }
      });
      
      return () => unsubscribe();
    }
  }, [entityType, userId]);

  // Templates for empty CSV files
  const csvTemplates = {
    services: [
      { 
        title: '', 
        description: '', 
        price: '', 
        discountPercent: '', 
        category: '', 
        duration: '', 
        microservice_ids: '' // New column for microservice IDs (comma separated)
      }
    ],
    microservices: [
      { title: '', description: '', category: '' } // Price removed
    ]
  };

  // Handler for CSV download
  const handleDownloadCsv = () => {
    // Choose the appropriate template
    const template = csvTemplates[entityType] || [];
    
    // Convert to CSV with PapaParse
    const csv = Papa.unparse(template);
    
    // Create a blob and a download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link for download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${entityType}_template.csv`);
    
    // Append and click the link
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handler for CSV upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccessCount(0);

    // Parse CSV file
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          setError('Error parsing CSV file: ' + results.errors[0].message);
          setUploading(false);
          return;
        }

        try {
          // Data reference based on entityType
          const baseRef = ref(database, `/World/${userId}/${entityType}`);
          let successItems = 0;

          // Add each row to the database
          for (const row of results.data) {
            // Skip empty rows or rows without title
            if (!row.title || row.title.trim() === '') continue;

            // Create a new object with correct data types
            const newItem = {
              title: row.title.trim(),
              description: row.description || '',
              updatedAt: Date.now(),
              createdAt: Date.now()
            };

            // Specific fields depending on entity type
            if (entityType === 'services') {
              newItem.imageUrl = '';  // Default value for new services
              
              // Process microservices if IDs were provided
              newItem.microservices = [];
              if (row.microservice_ids) {
                const msIds = row.microservice_ids.split(',').map(id => id.trim());
                
                // Filter valid microservices
                const validMicroservices = microservices.filter(ms => msIds.includes(ms.id));
                
                // Extract only necessary data
                newItem.microservices = validMicroservices.map(ms => ({
                  id: ms.id,
                  title: ms.title,
                  description: ms.description || '',
                  price: ms.price,
                  category: ms.category || ''
                }));
                
                // Calculate price summary
                const microservicesTotal = newItem.microservices.reduce((sum, ms) => sum + ms.price, 0);
                newItem.microservicesTotal = microservicesTotal;
              }
            } else if (entityType === 'microservices') {
              // For microservices we set the price to 0
              newItem.price = 0;
            }

            // Common fields
            if (entityType === 'services' && row.price) {
              const price = parseFloat(row.price.replace(',', '.'));
              newItem.price = isNaN(price) ? 0 : price;
            } else if (entityType === 'services') {
              newItem.price = 0;
            }

            if (row.category) {
              newItem.category = row.category.trim();
            }

            // Specific fields for services
            if (entityType === 'services') {
              if (row.discountPercent) {
                const discount = parseFloat(row.discountPercent.replace(',', '.'));
                newItem.discountPercent = isNaN(discount) ? 0 : discount;
              } else {
                newItem.discountPercent = 0;
              }

              if (row.duration) {
                const duration = parseInt(row.duration);
                newItem.duration = isNaN(duration) ? null : duration;
              } else {
                newItem.duration = null;
              }
              
              // Calculate total prices
              const totalPrice = newItem.price + (newItem.microservicesTotal || 0);
              const finalPrice = totalPrice - (totalPrice * (newItem.discountPercent / 100));
              
              newItem.totalPrice = totalPrice;
              newItem.finalPrice = finalPrice;
            }

            // Add to database
            const newRef = push(baseRef);
            await set(newRef, newItem);
            successItems++;
          }

          setSuccessCount(successItems);
          if (onSuccess) onSuccess(successItems);
        } catch (err) {
          setError('Error uploading: ' + err.message);
          if (onError) onError(err);
        } finally {
          setUploading(false);
        }
      },
      error: (err) => {
        setError('Error parsing file: ' + err.message);
        setUploading(false);
        if (onError) onError(err);
      }
    });

    // Reset file input
    event.target.value = '';
  };

  return (
    <div className="csv-handler">
      <div className="csv-actions">
        <button 
          className="btn-download-csv" 
          onClick={handleDownloadCsv}
          title="Download CSV Template"
        >
          Download CSV Template
        </button>
        
        <div className="csv-upload-container">
          <input 
            type="file" 
            id={`csv-upload-${entityType}`}
            accept=".csv" 
            onChange={handleFileUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label 
            htmlFor={`csv-upload-${entityType}`}
            className={`btn-upload-csv ${uploading ? 'uploading' : ''}`}
          >
            {uploading ? 'Uploading...' : 'Import CSV File'}
          </label>
        </div>
      </div>

      {error && <div className="csv-error">{error}</div>}
      {successCount > 0 && (
        <div className="csv-success">
          {successCount} {entityType === 'services' ? 'Services' : 'Microservices'} successfully imported!
        </div>
      )}
      
      {entityType === 'services' && (
        <div className="csv-help">
          <h5>Note about Microservices:</h5>
          <p>To add microservices to a service, enter the microservice IDs in the "microservice_ids" column, separated by commas.</p>
          <p>Available Microservice IDs:</p>
          <ul className="microservice-id-list">
            {microservices.length > 0 ? (
              microservices.map(ms => (
                <li key={ms.id}>
                  <code>{ms.id}</code> - {ms.title}
                </li>
              ))
            ) : (
              <li>No microservices available yet</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CsvHandler; 